'use client';

import Icon from '@/components/ui/AppIcon';

interface PartnerPerformanceProps {
  dateRange: string;
}

const PartnerPerformance = ({ dateRange }: PartnerPerformanceProps) => {
  const partners = [
    {
      name: 'Skyscanner',
      type: 'Flight Bookings',
      icon: 'GlobeAltIcon',
      color: 'blue',
      totalBookings: 35,
      totalEarnings: 654.80,
      avgCommission: 18.71,
      conversionRate: 5.2,
      topRoutes: [
        { route: 'London → Dubai', bookings: 12, earnings: 216.48 },
        { route: 'Manchester → New York', bookings: 8, earnings: 188.48 },
        { route: 'Birmingham → Paris', bookings: 7, earnings: 98.00 },
      ],
    },
    {
      name: 'Booking.com',
      type: 'Hotel Bookings',
      icon: 'BuildingOffice2Icon',
      color: 'green',
      totalBookings: 28,
      totalEarnings: 509.60,
      avgCommission: 18.20,
      conversionRate: 4.8,
      topRoutes: [
        { route: 'Dubai Hotels', bookings: 10, earnings: 192.00 },
        { route: 'London Hotels', bookings: 9, earnings: 172.80 },
        { route: 'Paris Hotels', bookings: 5, earnings: 96.00 },
      ],
    },
    {
      name: 'Expedia',
      type: 'Holiday Packages',
      icon: 'SparklesIcon',
      color: 'purple',
      totalBookings: 24,
      totalEarnings: 292.40,
      avgCommission: 12.18,
      conversionRate: 3.9,
      topRoutes: [
        { route: 'Maldives Packages', bookings: 8, earnings: 148.00 },
        { route: 'Caribbean Packages', bookings: 7, earnings: 88.80 },
        { route: 'European Tours', bookings: 5, earnings: 55.60 },
      ],
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
      green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {partners.map((partner, index) => {
        const colorClasses = getColorClasses(partner.color);
        return (
          <div
            key={index}
            className={`bg-white border ${colorClasses.border} rounded-lg p-6`}
          >
            {/* Partner Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-lg ${colorClasses.bg}`}>
                  <Icon name={partner.icon as any} size={32} className={colorClasses.text} />
                </div>
                <div>
                  <h3 className="font-black text-xl text-foreground">{partner.name}</h3>
                  <p className="text-sm text-muted-foreground">{partner.type}</p>
                </div>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-primary hover:bg-primary/5 rounded-lg transition-colors">
                <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                <span>View Details</span>
              </button>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-surface rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Bookings</p>
                <p className="text-2xl font-black text-foreground">{partner.totalBookings}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Earnings</p>
                <p className="text-2xl font-black text-green-600">£{partner.totalEarnings.toFixed(2)}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Avg Commission</p>
                <p className="text-2xl font-black text-foreground">£{partner.avgCommission.toFixed(2)}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Conversion Rate</p>
                <p className="text-2xl font-black text-foreground">{partner.conversionRate}%</p>
              </div>
            </div>

            {/* Top Routes */}
            <div>
              <h4 className="font-bold text-foreground mb-3">Top Performing Routes</h4>
              <div className="space-y-2">
                {partner.topRoutes.map((route, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-surface rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-white rounded-full text-xs font-bold text-foreground">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-foreground">{route.route}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Bookings</p>
                        <p className="font-bold text-foreground">{route.bookings}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Earnings</p>
                        <p className="font-bold text-green-600">£{route.earnings.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PartnerPerformance;
